<template>
  <div ref="wrapper">
    <svg
      :id="id"
      class="histogram-chart"
      :width="chartWidth"
      :height="height - margin"
    />
  </div>
</template>

<script>
import * as d3 from 'd3'
import { scaleLinear, scaleBand } from 'd3-scale'
import { axisLeft, axisBottom } from 'd3-axis'
import { mapState, mapGetters } from 'vuex'
import { numberWithCommas, convert } from '@/utils/main_helper'

var prevDiscoveryId = ''
var payload = {
  organization_id: '',
  discovery_id: '',
  filterLabels: {
    size: [],
    age: [],
    file_extension: []
  }
}
export default {
  name: 'Histogram',
  props: {
    data: {
      type: [Object, Array],
      default: null
    },
    xAxix: {
      type: Array,
      default: () => []
    },
    yAxix: {
      type: Array,
      default: () => []
    },
    xAxisKey: {
      type: String,
      default: ''
    },
    yAxisKey: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: 'inherit'
    },
    height: {
      type: [String, Number],
      default: 350
    },
    color: {
      type: String,
      default: ''
    },
    discoveryId: {
      type: String,
      default: ''
    },
    scanStartTime: {
      type: [String, Number],
      default: ''
    },
    scanEndTime: {
      type: [String, Number],
      default: ''
    },
    drives: {
      type: Array,
      default: []
    },
    id: {
      type: [String, Number],
      default: null
    },
    filterId: {
      type: String
    }
  },
  data () {
    return {
      chartWidth: null,
      margin: 70,
      chart: {
        yScale: null,
        xScale: null
      }
    }
  },
  computed: {
    ...mapGetters({
      currentOrganization: 'currentOrganization',
      colorPalette: 'colorPaletteServices/theme'
    }),
    ...mapState('discoveryServices', ['labelIndexs', 'selectedSize', 'selectedAge', 'selectedFileExtention', 'sizeFilters', 'lastModifiedFilters', 'extensionFilters']),

    filter () {
      return this[this.filterId]
    }
  },
  mounted () {
    payload.file_extension && delete payload.file_extension
    payload.age && delete payload.age
    payload.size && delete payload.size

    payload.filterLabels.age = this.lastModifiedFilters
    payload.filterLabels.size = this.sizeFilters
    payload.filterLabels.file_extension = this.extensionFilters
    if (this.selectedAge && this.selectedAge.length) {
      payload.age = this.selectedAge
    }
    if (this.selectedFileExtention && this.selectedFileExtention.length) {
      payload.file_extension = this.selectedFileExtention
    }
    if (this.selectedSize && this.selectedSize.length) {
      payload.size = this.selectedSize
    }

    if (this.width === 'inherit') {
      this.chartWidth = this.$refs.wrapper.getBoundingClientRect().width
    } else this.chartWidth = this.width
    this.$root.$on('Histogram', (id) => {
      this.removeFiltersFromPayload(id)
    })
    this.render()
    this.$store.subscribe(mutation => {
      if (mutation.type == 'discoveryServices/SET_ANALYSIS') {
        this.removeFilters()
      }
    })
  },
  methods: {
    render (update = false) {
      const nullishHeight = 5
      const margin = this.margin
      const width = this.chartWidth - 2 * margin
      const height = this.height - 2 * margin
      prevDiscoveryId = this.discoveryId
      // click Flag
      const clickFlag = []
      // Tooltip
      let tooltip
      if (!d3.select('.histogram-tooltip').empty()) {
        tooltip = d3.select('.histogram-tooltip')
      } else {
        tooltip = d3.select('body').append('div').attr('class', 'histogram-tooltip')
      }
      this.yScale = scaleLinear()
        .range([height, 0])
        .domain([0, Math.max(...this.yAxix)]).nice()

      this.xScale = scaleBand()
        .range([0, width])
        .domain(this.xAxix.map((s) => s))
        .padding(0.8)

      let svg
      if (update) {
        svg = d3.select(`#${this.id} g`).node() ? d3.select(`#${this.id} g`) : d3.select(`#${this.id}`).append('g')
        const bars = svg.selectAll('rect')
        bars.remove()

        // Y Scale
        svg.select('.y-scale')
          .transition()
          .duration(1000)
          .call(axisLeft(this.yScale)
            .tickFormat(d => {
              if (d >= Math.pow(1000, 2)) {
                const value = d / (Math.pow(1000, 2))
                return `${value.toFixed(2)} M`
              } else if (d >= 1000) {
                const value = d / 1000
                return `${value} K`
              } else {
                return numberWithCommas(d)
              }
            }))

        // X Scale
        svg.select('.x-scale')
          .transition()
          .duration(1000)
          .call(axisBottom(this.xScale))
          .attr('transform', `translate(5, ${height})`)
          .style('text-anchor', 'end')
          .selectAll('text')
          .attr('transform', 'rotate(-30)')
          .on('end', () => {
            setTimeout(() => {
              svg.selectAll('.x-scale .tick text')
                .attr('id', this.id + 'x')
                .on('click', (d, index) => {
                  const id = this.id
                  if (!payload[id]) payload[id] = []
                  const dObj = id == 'file_extension' ? { extension: d } : { label: d }
                  this.handleClickEvent(d3, index, clickFlag, dObj)
                })
            }, 500)
          })

        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', 0 - 80)
          .attr('x', 0 - (height / 2))
          .attr('style', 'fill: #212338')
          .attr('dy', '1em')
          .style('text-anchor', 'middle')

        svg.selectAll()
          .data(this.data)
          .enter()
          .append('rect')
          .attr('x', (s) => this.xScale(s[this.xAxisKey]))
          .attr('y', (s) => {
            if (!(height - this.yScale(s[this.yAxisKey]))) {
              return this.yScale(s[this.yAxisKey]) - nullishHeight
            } return this.yScale(s[this.yAxisKey])
          })
          .attr('height', (s) => height - this.yScale(s[this.yAxisKey]) || nullishHeight)
          .attr('width', this.xScale.bandwidth())
          .attr('style', `fill: ${this.color || this.colorPalette.histogramBars}`)
          .attr('id', this.id)
          .attr('class', 'bar')
          .classed('disabled', (d) => d.disabled)
          .classed('inactive', (d) => !Object.prototype.hasOwnProperty.call(d, 'disabled') || d.disabled === undefined)
          .on('mouseenter', (d) => {
            this.handleMouseEnter(tooltip, d, d3.event)
          })
          .on('mouseleave', (d) => {
            this.handleMouseLeave(tooltip, d)
          })
          .on('click', (d, index) => {
            if (!payload[this.id]) payload[this.id] = []
            this.handleClickEvent(d3, index, clickFlag, d)
          })
      } else {
        svg = d3.select(`#${this.id}`).append('g').attr('transform', `translate(${margin}, ${margin / 5})`)
        svg.attr('transform', 'translate(' + 50 + ',' + 10 + ')')
        svg.append('g')
          .attr('class', 'grid')
          .call(axisLeft()
            .scale(this.yScale)
            .tickSize(-width, 0, 0)
            .tickFormat(''))

        // Y Scale
        svg.append('g')
          .call(axisLeft(this.yScale)
            .tickFormat(d => {
              if (d >= Math.pow(1000, 2)) {
                const value = d / (Math.pow(1000, 2))
                return `${value.toFixed(2)} M`
              } else if (d >= 1000) {
                const value = d / 1000
                return `${value} K`
              } else {
                return numberWithCommas(d)
              }
            }))
          .attr('class', 'y-scale')

        // X Scale
        svg.append('g')
          .call(axisBottom(this.xScale))
          .attr('transform', `translate(5, ${height})`)
          .attr('class', 'x-scale')
          .style('text-anchor', 'end')
          .selectAll('text')
          .attr('transform', 'rotate(-30)')

        svg.selectAll('.x-scale .tick text')
          .attr('id', this.id + 'x')
          .on('click', (d, index) => {
            const id = this.id
            if (!payload[id]) payload[id] = []
            const dObj = id == 'file_extension' ? { extension: d } : { label: d }
            this.handleClickEvent(d3, index, clickFlag, dObj)
          })

        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', 0 - 80)
          .attr('x', 0 - (height / 2))
          .attr('style', 'fill: #212338')
          .attr('dy', '1em')
          .style('text-anchor', 'middle')

        svg.selectAll()
          .data(this.data)
          .enter()
          .append('rect')
          .attr('x', (s) => this.xScale(s[this.xAxisKey]))
          .attr('y', (s) => {
            if (!(height - this.yScale(s[this.yAxisKey]))) {
              return this.yScale(s[this.yAxisKey]) - nullishHeight
            } return this.yScale(s[this.yAxisKey])
          })
          .attr('height', (s) => height - this.yScale(s[this.yAxisKey]) || nullishHeight)
          .attr('width', this.xScale.bandwidth())
          .attr('style', `fill: ${this.color || this.colorPalette.histogramBars}`)
          .attr('id', this.id)
          .attr('class', 'bar')
          .classed('disabled', (d) => d.disabled)
          .classed('inactive', (d) => !Object.prototype.hasOwnProperty.call(d, 'disabled') || d.disabled === undefined)
          .on('mouseenter', (d) => {
            this.handleMouseEnter(tooltip, d, d3.event)
          })
          .on('mouseleave', (d) => {
            this.handleMouseLeave(tooltip, d)
          })
          .on('click', (d, index) => {
            if (!payload[this.id]) payload[this.id] = []
            this.handleClickEvent(d3, index, clickFlag, d)
          })
      }

      svg.on('mousemove', (d) => {
        this.handleMouseMove(d3.event, tooltip)
      })
    },
    renderTooltipContent (tooltip, data) {
      tooltip.append(() => {
        tooltip.node().innerHTML = ''
        const ul = document.createElement('ul')
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key) && ['label', 'total_count', 'total_size', 'extension'].includes(key)) {
            const li = document.createElement('li')
            li.innerHTML = `${this.changeLabel(key)}: ${this.tootipLabel(data, key)}`
            ul.append(li)
          }
        }
        return ul
      })
    },
    changeLabel (key) {
      switch (key) {
        case 'total_count':
          return 'Count'
        case 'total_size':
          return 'Volume'
        case 'extension':
          return 'Extension'
        case 'label':
          return this.id == 'age' ? 'Modified Bin' : 'Volume Bin'
      }
    },
    getXLabel (id) {
      switch (id) {
        case 'age':
          return 'Age'
        case 'size':
          return 'Volume'
        case 'file_extension':
          return 'Type'
      }
    },
    chartId () {
      let flag = ''
      switch (this.id) {
        case 'file_extension':
          flag = 'extension'
          break
        case 'age':
          flag = 'last_modified'
          break
        default:
          flag = 'size'
          break
      }
      return flag
    },
    extensionOrLabel (d, index) {
      if (this.id === 'size') { return this.labelIndexs.size[d.label] } else if (this.id === 'age') { return this.labelIndexs.age.indexOf(d.label) } else if (this.id === 'file_extension') { return d.extension }
      return ''
    },
    removeFiltersFromPayload (data) {
      let indexInPayload = -1
      if (data.field === 'file_extension') {
        indexInPayload = payload[data.field].indexOf(data.value)
      } else if (data.field === 'size') {
        const index = this.labelIndexs[data.field][data.value]
        indexInPayload = payload[data.field]?.indexOf(index)
      } else {
        const index = this.labelIndexs[data.field].indexOf(data.value)
        indexInPayload = payload[data.field]?.indexOf(index)
      }
      if (indexInPayload != -1) {
        payload[data.field] && payload[data.field].splice(indexInPayload, 1)
        payload.filterLabels[data.field] && payload.filterLabels[data.field].splice(indexInPayload, 1)
      }
      // payload[data.field] && payload[data.field].length || delete payload[data.field]
    },

    removeFilters () {
      const elementItems = d3.selectAll(`#${this.id}`)._groups[0]
      const elements = []
      elementItems.forEach(x => elements.push(x))
      elements.shift()
      elements.filter(x => !this.filter.includes(x.__data__[this.xAxisKey])).map(x => x.style.opacity = 0.5)
      elements.filter(x => this.filter.includes(x.__data__[this.xAxisKey])).map(x => x.style.opacity = 1.0)
    },

    handleClickEvent (d3, index, clickFlag, d) {
      if (payload[this.id] && payload[this.id].includes(this.extensionOrLabel(d, index))) {
        if (d.extension != '') {
          d3.selectAll(`#${this.id}`)._groups[0][++index].style.opacity = 0.5
          payload.filterLabels[this.id].splice(payload[this.id].indexOf(this.extensionOrLabel(d, index)), 1)
          payload[this.id].splice(payload[this.id].indexOf(this.extensionOrLabel(d, index)), 1)
          payload[this.id].length || delete payload[this.id]
          delete payload.chartId
        }
      } else {
        d3.selectAll(`#${this.id}`)._groups[0][++index].style.opacity = 1
        if (d.extension != '') {
          payload.filterLabels[this.id].push(d.label || d.extension)
          payload[this.id].push(this.extensionOrLabel(d, index))
        }
      }
      payload.chartId = this.chartId()
      payload.organization_id = this.currentOrganization.entity_id

      if (this.discoveryId == 'last_24_hours') {
        delete payload.discovery_id
        payload.end_datetime = this.scanEndTime
        payload.start_datetime = this.scanStartTime
      } else {
        delete payload.end_datetime
        delete payload.start_datetime
        payload.discovery_id = this.discoveryId
      }

      if (this.drives.length) {
        payload.drive = this.drives
      }
      this.$store.dispatch('discoveryServices/filterDiscoveryBars', { payload })
    },
    handleMouseEnter (tooltip, data, event) {
      const barNode = event.target.getBoundingClientRect()
      const tooltipNode = tooltip.node().getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      if (!tooltip.classed('show')) {
        tooltip.classed('show', true)
      }

      this.renderTooltipContent(tooltip, data)
      tooltip
        .style('left', barNode.left + barNode.width + 20 + 'px')
        .style('top', barNode.y + scrollTop + (barNode.height - tooltipNode.height) / 2 + 'px')
    },
    handleMouseLeave (tooltip) {
      tooltip.classed('show', false)
    },
    handleMouseMove (event, tooltip) {
      if (!event.target.classList.contains('bar')) {
        tooltip.classed('show', false)
      }
    },
    tootipLabel (data, key) {
      if (key === 'total_size') { return this.volumeNumberFormat(data[key]) } else if (key === 'total_count') { return numberWithCommas(data[key]) } else return data[key]
    },
    volumeNumberFormat (number) {
      return convert(number, 'GB', 1000)
    }
  },
  watch: {
    data () {
      if (this.discoveryId != prevDiscoveryId) {
        payload = {
          organization_id: '',
          discovery_id: '',
          filterLabels: {
            size: [],
            age: [],
            file_extension: []
          }
        }
      }
      this.render(true)
    }
  }
}
</script>

<style lang="scss">
  .histogram-chart {
    user-select: none;
    .y-scale {
      path, line {
        stroke: rgba(0, 0, 0, 0.07);
      }
      text {
        fill: #212338;
        font-weight: 600;
        font-size: 12px;
      }
    }
    .x-scale {
      path, line {
        stroke: rgba(0, 0, 0, 0.07);
      }
      text {
        fill: #212338;
        font-weight: 600;
        letter-spacing: 0.001em;
        font-size: 12px;
      }
    }
    .bar {
      cursor: pointer;
      transition: 1s;
      &.inactive {
        opacity: 0.5;
      }
      &.disabled {
        fill: #ccc !important;
        opacity: 0.5;
      }
    }
    .tick{
      cursor: pointer;
    }
    .grid {
      color: rgba(0, 0, 0, 1);
      .tick:last-child {
         color: snow !important;
      }
      .tick {
        color: rgba(0, 0, 0, 0.07);
      }
      text {
        fill: #7c7c7c;
        font-size: 12px;
      }
    }
  }
</style>
